import { Clock, Users, Library, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { routeBuilders } from '../routes';
import { slugify } from '../../utils/slug';

type HomeStory = {
  id: string;
  title: string;
  slug?: string | null;
  author: string;
  creatorSlug?: string | null;
  creatorName?: string | null;
  coverImage: string;
  duration: string;
  totalChapters: number;
  genreIds: string[];
  listens?: number;
  releaseSchedule?: string | null;
};

type ContinueListeningItem = {
  seriesId: string;
  seriesSlug?: string | null;
  story: HomeStory | null;
  coverImage: string;
  creatorName?: string | null;
  creatorSlug?: string | null;
  episodeId: string;
  episodeTitle: string;
  episodeSlug?: string | null;
  episodeNumber?: number | null;
  positionSeconds: number;
  durationSeconds?: number | null;
  lastListenedAt?: string | null;
};

type CreatorCard = {
  id: string;
  displayName: string;
  avatarUrl: string;
  followerCount: number;
  seriesCount: number;
  slug: string;
};

interface HomeProps {
  stories: HomeStory[];
  continueListening: ContinueListeningItem[];
  newCreators: CreatorCard[];
  onPlayStory: (story: HomeStory, options?: { startEpisodeId?: string }) => void;
  onLikeStory: (storyId: string) => void;
  likedStories: Set<string>;
}

const formatTime = (seconds?: number | null) => {
  if (!seconds || seconds <= 0) return '0:00';
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

const progressPercent = (position?: number, duration?: number) => {
  if (!duration || duration <= 0 || !position || position <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round((position / duration) * 100)));
};

export function Home({
  stories,
  continueListening,
  newCreators,
  onPlayStory,
  onLikeStory: _onLikeStory,
  likedStories: _likedStories,
}: HomeProps) {
  const latestSeries = stories.slice(0, 6);
  const currentProgress = continueListening[0] ?? null;

  const resolveSeriesPath = (story?: HomeStory | null, slugOverride?: string | null) => {
    const slug = slugOverride ?? story?.slug ?? (story?.title ? slugify(story.title) : null);
    return slug ? routeBuilders.seriesPublic(slug) : routeBuilders.home();
  };

  const resolveCreatorPath = (creatorSlug?: string | null) =>
    creatorSlug ? routeBuilders.creatorPublic(creatorSlug) : null;

  const handleResume = () => {
    if (currentProgress?.story) {
      onPlayStory(currentProgress.story, { startEpisodeId: currentProgress.episodeId });
    }
  };

  return (
    <div className="page-shell">
      <div className="page-header">
        <h1 className="text-4xl font-bold">Latest Series</h1>
        <p className="text-base-content/60">Latest audio stories to listen. Curated and hand-picked.</p>
      </div>

      <section className="mb-10">
        {latestSeries.length === 0 ? (
          <div className="text-base-content/50 text-sm">No series published yet.</div>
        ) : (
          <div className="flex gap-4 overflow-x-auto pb-3 -mx-6 px-6 scrollbar-hide">
            {latestSeries.map((story) => {
              const seriesPath = resolveSeriesPath(story);
              return (
                <Link
                  key={story.id}
                  to={seriesPath}
                  className="group w-52 flex-shrink-0"
                >
                  <div className="relative aspect-[4/5] rounded-3xl overflow-hidden bg-base-200 shadow-sm transition-transform duration-300 group-hover:-translate-y-1">
                    <img
                      src={story.coverImage}
                      alt={story.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-neutral/80 via-neutral/40 to-transparent" />
                    <div className="absolute inset-0 p-4 flex flex-col justify-end gap-2">
                      <span className="text-[11px] uppercase tracking-wide text-neutral-content/70">New Series</span>
                      <h3 className="text-neutral-content text-lg font-semibold leading-tight line-clamp-2">
                        {story.title}
                      </h3>
                      <p className="text-sm text-neutral-content/80 line-clamp-1">{story.author}</p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>

      {currentProgress && (
        <section className="mb-12">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold">Continue Listening</h2>
            <p className="text-sm text-base-content/60">Jump back into your latest episode</p>
          </div>
          <div
            className="rounded-3xl bg-neutral text-neutral-content p-6 shadow-lg cursor-pointer transition-transform hover:-translate-y-0.5"
            onClick={handleResume}
          >
            <div className="flex flex-col gap-4 md:flex-row md:items-center">
              <div className="w-24 h-24 rounded-2xl overflow-hidden bg-neutral-content/10 flex-shrink-0">
                <img
                  src={currentProgress.coverImage}
                  alt={currentProgress.episodeTitle}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1 min-w-0 space-y-2">
                <p className="text-xs uppercase tracking-wide text-neutral-content/70 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Episode {currentProgress.episodeNumber ?? '—'}
                </p>
                <h3 className="text-xl font-semibold leading-tight line-clamp-1">
                  {currentProgress.episodeTitle}
                </h3>
                <div className="flex items-center gap-3 text-sm text-neutral-content/80">
                  <Link
                    to={resolveSeriesPath(currentProgress.story, currentProgress.seriesSlug)}
                    onClick={(e) => e.stopPropagation()}
                    className="hover:text-neutral-content transition-colors truncate"
                  >
                    {currentProgress.story?.title ?? 'View series'}
                  </Link>
                  {(() => {
                    const creatorPath = resolveCreatorPath(
                      currentProgress.creatorSlug ?? currentProgress.story?.creatorSlug,
                    );
                    return creatorPath ? (
                      <Link
                        to={creatorPath}
                        onClick={(e) => e.stopPropagation()}
                        className="hover:text-neutral-content transition-colors truncate"
                      >
                        {currentProgress.creatorName ?? currentProgress.story?.author}
                      </Link>
                    ) : (
                      <span className="truncate">
                        {currentProgress.creatorName ?? currentProgress.story?.author}
                      </span>
                    );
                  })()}
                </div>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleResume();
                }}
                className="btn btn-primary btn-sm"
              >
                Resume
              </button>
            </div>
            <div className="mt-4">
              <div className="h-2 bg-neutral-content/20 rounded-full overflow-hidden">
                <div
                  className="h-full bg-neutral-content rounded-full transition-all"
                  style={{ width: `${progressPercent(currentProgress.positionSeconds, currentProgress.durationSeconds)}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-neutral-content/70 mt-2">
                <span>
                  {formatTime(currentProgress.positionSeconds)} / {formatTime(currentProgress.durationSeconds)}
                </span>
                {currentProgress.durationSeconds ? (
                  <span>
                    {formatTime(
                      Math.max(
                        (currentProgress.durationSeconds ?? 0) - (currentProgress.positionSeconds ?? 0),
                        0,
                      ),
                    )}{' '}
                    left
                  </span>
                ) : (
                  <span>In progress</span>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {newCreators.length > 0 && (
        <section className="mb-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold">Meet New Creators</h2>
            <p className="text-sm text-base-content/60">Say hi to the latest storytellers</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {newCreators.map((creator) => {
              const creatorPath = routeBuilders.creatorPublic(creator.slug);
              return (
                <Link
                  key={creator.id}
                  to={creatorPath}
                  className="group rounded-2xl border border-base-200 bg-base-100 p-4 flex items-center gap-4 hover:border-base-300 hover:shadow-md transition-all"
                >
                  <div className="w-14 h-14 rounded-full overflow-hidden bg-base-200 ring-2 ring-base-100 shadow-sm">
                    <img
                      src={creator.avatarUrl}
                      alt={creator.displayName}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-base-content truncate">{creator.displayName}</p>
                    <div className="flex items-center gap-3 text-sm text-base-content/70 mt-1">
                      <span className="flex items-center gap-1">
                        <Library className="w-4 h-4" />
                        {creator.seriesCount} series
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {creator.followerCount} followers
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-base-content/50 group-hover:text-base-content/70" />
                </Link>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
